import { Carousel } from '@components/common/Carousel';
import * as Skeleton from '@components/Skeleton/Skeleton';
import { cdnImageUrl } from '@utils/image';
import React, { useEffect, useRef, useState } from 'react';
import { ReactComponent as UsersIcon } from '@assets/images/app/users.svg';
import { ReactComponent as MembershipRibbon } from '@assets/images/app/membership-ribbon-big.svg';
import { ReactComponent as AppOnlyBadge } from '@assets/images/app/app-only-badge.svg';

const IMPRESSIONS_WORDS: { [key: string]: { prefix: string; highlight: string; suffix: string } } = {
  payment24h: { prefix: '오늘', highlight: '{value}명이 구매', suffix: '했어요' },
  payment30d: { prefix: '최근', highlight: '{value}명이 구매', suffix: '했어요' },
  payment90d: { prefix: '최근 3개월 간', highlight: '{value}명이 구매', suffix: '했어요' },
  repurchase: { prefix: '이 상품을', highlight: '{value}명이 재구매', suffix: '했어요' },
  experienced: { prefix: '이 상품을', highlight: '{value}명이 체험', suffix: '했어요' },
  view7d: { prefix: '최근', highlight: '{value}명이 관심', suffix: '있어요' },
  view30d: { prefix: '최근', highlight: '{value}명이 관심', suffix: '있어요' },
  view1d: { prefix: '오늘', highlight: '{value}명이 관심', suffix: '있어요' },
};

const renderImpressionsWord = (impressions?: { value: number; unit: string }, show?: boolean) => {
  if (!impressions || !impressions.unit || !impressions.value) {
    return <></>;
  }

  const impressionWord = IMPRESSIONS_WORDS[impressions.unit];

  if (!impressionWord) {
    return <></>;
  }

  return (
    <div
      style={{
        transition: 'all 0.3s linear',
        opacity: show ? 1 : 0,
        borderRadius: '40px',
        background: 'var(--Neutral-100, #242424)',
        boxShadow: '0px 0px 2px 0px rgba(0, 0, 0, 0.02), 0px 6px 14px 0px rgba(0, 0, 0, 0.06)',
        position: 'absolute',
        top: 0,
        bottom: 0,
        right: 0,
        left: 0,
        transform: show ? 'translateY(0)' : 'translateY(120px)',
        margin: 'auto',
        width: 'fit-content',
        height: 'fit-content',
        zIndex: 1000,
        padding: '10px 16px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '8px',
      }}>
      <UsersIcon />
      <div
        style={{
          color: 'var(--White-100, #FFF)',
          fontFamily: 'Pretendard',
          fontSize: '12px',
          fontStyle: 'normal',
          fontWeight: 500,
          display: 'flex',
          gap: '4px',
        }}>
        <span>{impressionWord.prefix}</span>
        <span
          style={{
            color: 'var(--Primary-70, #FF77B1)',
            fontFamily: 'Pretendard',
            fontSize: '12px',
            fontStyle: 'normal',
            fontWeight: 700,
          }}>
          {impressionWord.highlight.replace('{value}', `${impressions.value}`)}
        </span>
        <span>{impressionWord.suffix}</span>
      </div>
    </div>
  );
};

export const ProductDetailImages: React.FC<{
  imageUrls?: string[];
  closed?: boolean;
  closeText?: string;
  impressions?: { value: number; unit: string };
  appliedMembership?: boolean;
  appOnly?: boolean;
}> = (props) => {
  const [currentPage, setCurrenPage] = useState<number>(1);
  const [show, setShow] = useState<boolean>(false);
  const { imageUrls, impressions, appliedMembership, appOnly } = props;
  const timerRef = useRef<any>();

  useEffect(() => {
    if (imageUrls && impressions) {
      setShow((value) => {
        if (!value) {
          return true;
        }
        return value;
      });

      if (!timerRef.current) {
        timerRef.current = setTimeout(() => {
          setShow(false);
          clearTimeout(timerRef.current);
          timerRef.current = undefined;
        }, 5000);
      }
    }
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        setShow(false);
      }
    };
  }, [imageUrls, impressions]);

  if (!imageUrls) {
    return <Skeleton.Base style={{ width: '100%', aspectRatio: 1 }} />;
  }

  return (
    <div>
      <div
        style={{
          width: '100%',
          alignItems: 'center',
          justifyContent: 'center',
          aspectRatio: 1,
          position: 'relative',
        }}>
        {appliedMembership && (
          <MembershipRibbon style={{ position: 'absolute', left: 0, bottom: '16px', zIndex: 1000 }} />
        )}
        {appOnly && (
          <AppOnlyBadge
            style={{ position: 'absolute', right: '16px', bottom: '16px', zIndex: 1000, width: '64px', height: '64px' }}
          />
        )}
        {renderImpressionsWord(impressions, show)}
        <Carousel
          options={{
            align: 'start',
            containScroll: 'trimSnaps',
          }}
          onChangeSlide={(currentSlide: number) => {
            setCurrenPage(currentSlide + 1);
          }}>
          {imageUrls.map((imageUrl: string, index: number) => {
            return (
              <img
                key={`product-image-${index}`}
                width={'100%'}
                src={cdnImageUrl(imageUrl as string)}
                style={{ flex: '0 0 auto', objectFit: 'cover', aspectRatio: 1 }}
                alt=""
              />
            );
          })}
        </Carousel>
        {imageUrls.length > 1 && (
          <div
            style={{
              display: 'flex',
              position: 'absolute',
              bottom: '12px',
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: 'rgba(0,0,0,.6)',
              height: '24px',
              borderRadius: '12px',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '6px 10px',
            }}>
            <div
              style={{
                alignSelf: 'center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--White-100, #FFF)',
                fontFamily: 'Pretendard',
                fontSize: '12px',
                fontStyle: 'normal',
                fontWeight: 500,
              }}>
              <span style={{ alignSelf: 'center' }}>{currentPage}</span>
              <span style={{ alignSelf: 'center', width: '8px', textAlign: 'center' }}>/</span>
              <span style={{ color: 'rgba(255, 255, 255, 0.6)', alignSelf: 'center' }}>{imageUrls.length}</span>
            </div>
          </div>
        )}
      </div>
      {props.closed && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            left: 0,
            background: 'var(--Black-50, rgba(0, 0, 0, 0.50))',
            color: 'white',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            fontSize: '20px',
            fontFamily: 'Pretendard',
            fontWeight: 700,
            aspectRatio: 1,
          }}>
          {props.closeText || '판매 종료'}
        </div>
      )}
    </div>
  );
};
